<template>
  <div class="task-card-wrapper" ref="taskCardWrapper">
    <b-card
      @click="navigate(cardData)"
      v-if="!isLoadingList"
      no-body
      class="task-cards-container"
      :class="{
        'high-priority': priority && statusTitle !== 'Complete'
      }"
      @mouseenter="showCardHover(cardData.id)"
      @mouseleave="undoCardHover(cardData.id)"
    >
      <div class="first-row">
        <div class="date-wrapper">
          <img src="@/assets/icons/timer_clock_outlined.svg" />
          <p>{{ formatDueDate(cardData.dueDate) }}</p>
        </div>
        <!-- <div class="high-priority-wrapper">
          <img v-show="priority" src="@/assets/icons/high_priority.svg" />
        </div> -->
        <div class="actions-wrapper">
          <div class="timer-wrapper">
            <p v-show="timerActive" class="time">{{ formattedTime }}</p>
            <p v-show="!timerActive" class="time-paused">
              {{ formattedTime }}
            </p>
            <template v-if="statusTitle !== 'Complete'">
              <b-spinner
                variant="primary"
                class="timer-spinner"
                v-if="timerApiInProgress"
              />
              <img
                v-b-tooltip.hover :title="tooltipText"
                v-else-if="timerActive"
                @click.stop="stopTimer"
                src="@/assets/icons/timer_pause_outlined.svg"
              />
              <img
                v-else
                v-b-tooltip.hover :title="tooltipText"
                @click.stop="startTimer"
                src="@/assets/icons/timer_play_outlined.svg"
              />
            </template>
          </div>
        </div>
      </div>
      <p class="primary--text font-family-regular w-100 card-title" data-cy="cardTitle">
        {{ cardData.title }}
      </p>
      <div class="d-flex align-items-center">
        <div class="tooltips">
          <b-avatar
            v-b-tooltip.hover.bottomright="{ customClass: 'txtx' }"
            :title="
              `${cardData.createdFor.firstName} ${cardData.createdFor.lastName}`
            "
            v-if="cardData.createdFor.avatarThumbnail"
            class=""
            size="2.8rem"
            :src="cardData.createdFor.avatarThumbnail"
          ></b-avatar>
          <b-avatar
            v-b-tooltip.hover.bottomright="{ customClass: 'txtx' }"
            :title="
              `${cardData.createdFor.firstName} ${cardData.createdFor.lastName}`
            "
            :text="userInitials(cardData.createdFor)"
            v-else
            class="tasks-card-avatar bg--primary"
            size="2.8rem"
          ></b-avatar>
        </div>
        <div class="icons-group d-flex">
          <div class="icon-group-1 d-flex" v-if="cardData.attachments > 0">
            <img
              src="@/assets/icons/attachment_light.svg"
              alt=""
              class="mr-2"
            />
            <h6 class="font-sm secondary--text font-family-regular">
              {{ cardData.attachments }}
            </h6>
          </div>

          <div class="icon-group-2 d-flex" v-if="cardData.checklists > 0">
            <img src="@/assets/icons/checklist_light.svg" alt="" class="mr-2" />
            <h6 class="font-sm secondary--text font-family-regular">
              {{ cardData.checklists }}
            </h6>
          </div>

          <div class="icon-group-3 d-flex" v-if="cardData.comments > 0">
            <img src="@/assets/icons/comment_light.svg" alt="" class="mr-2" />
            <h6 class="font-sm secondary--text font-family-regular">
              {{ cardData.comments }}
            </h6>
          </div>
        </div>
      </div>
    </b-card>

    <b-modal
      centered
      class="modal-padding"
      hide-footer
      v-model="showAuthError"
      hide-header
      no-close-on-backdrop
      no-close-on-esc
    >
      <b-container fluid>
        <b-button
          @click="reloadPage"
          class="d-flex position-absolute close-btn"
        >
          <img class="close-icon" src="@/assets/icons/close_sidebar.svg" alt />
        </b-button>
        <b-row align-v="center" class="px-2 py-2">
          <b-col md="9" class="d-flex mb-2">
            <h4 class="font-md font-family-medium mt-2">
              {{ authMessage }}
            </h4>
          </b-col>
          <b-button
            @click="reloadPage"
            style="top: 0.5rem"
            class="position-relative btn-danger px-5 py-3 btn-pill reduce-whitespace ml-4"
          >
            Close
          </b-button>
        </b-row>
      </b-container>
    </b-modal>
  </div>
</template>

<script>
import { patchTracker, postTracker } from "@/apis/common";
import { formatDueDate, formatDuration, ellipsify, isAdmin } from "@/utils/utils";
import moment from "moment";
import { mapState } from "vuex";
import { ACCEPTED, NOACTION, statuses } from "@/utils/constants";
import ClickManager from "@/components/clickmanager"
import Worker from "worker-loader!@/workers/TimerWorker.js";

export default {
  name: "TaskCards",
  props: {
    cardData: Object,
    list: [Array, String, Object],
    isToday: [Boolean],
    shouldPatch: Boolean,
    paused: Boolean,
    status: String,
    statusTitle: String,
    isLoadingList: Boolean,
    trackerID: String,
    time: Number,
    priority: Boolean,
    positionInfo: Object,
    navigateOnClick: {
      type: Boolean,
      default: true
    }
  },
  mounted() {
    this.clickManager = new ClickManager()
    this.clickManager.initialize(this, this.$refs.taskCardWrapper);
    this.handleCardOrder(this);
  },
  beforeDestroy() {
    // clean up the listeners
    this.clickManager.destroy()

    if (this.worker) {
      this.worker.terminate()
    } else {
      clearInterval(this.timer)
    }
  },
  methods: {
    ellipsify,
    onTimerStart() {
      if (window.Worker) {
        const worker = new Worker()
        this.worker = worker
        worker.postMessage({ 
          currentTime: this.currentTime,
          action: 'start'
        })
        worker.addEventListener("message", event => {
          let { currentTime } = event.data
          this.currentTime = currentTime
        })
      } else {
        this.timer = setInterval(() => {
        this.currentTime += 1;
        }, 1000);
      }
    },
    onTimerStop() {
      this.stopTimerThread()
    },
    stopTimerThread() {
      if (window.Worker) {
        if (this.worker) {
          const worker = this.worker
          worker.postMessage({
            action: 'stop'
          })
          worker.terminate()
        }
      } else {
        clearInterval(this.timer)
      }
    },
    stopTimer() {
      if (!this.canUserOperateTimer) {
        return
      }
      this.timerApiInProgress = true;
      const data = {
        action: "stop"
      };
      patchTracker(this.cardData.id, this.cardData.lastTracker.id, data).then(
        res => {
          this.timerActive = false;
          this.handleCardOrder(this);
          this.pause = true;
          this.cardData.lastTracker = res.data;
          this.cardData.trackersLog = res.data.trackersLog;
          this.onTimerStop();
          this.timerApiInProgress = false;
        }
      );
    },
    startTimer() {
      if (!this.canUserOperateTimer) {
        return
      }
      let createdAt = moment()
        .utc()
        .format("YYYY-MM-DDTHH:mm:ss.SSSSSS[Z]");
      const timerData = {
        creator: this.profileUserId,
        task: this.cardData.id,
        action: "start",
        startAt: createdAt
      };
      this.timerApiInProgress = true;
      postTracker(this.cardData.id, timerData)
        .then(res => {
          this.cardData.lastTracker = res.data;
          this.pause = false;
          this.timerActive = true;
          this.handleCardOrder(this);
          this.onTimerStart();
        })
        .catch(err => {
          // Top-center + solid + bigger + longer hold: the default toast is
          // small and tucked in a corner, easy to miss when this blocks the
          // action the user just took (e.g. the 2-running-timers cap).
          this.$bvToast.toast(
            err.response?.data?.detail || "Unable to start timer.",
            {
              title: "⚠ Timer",
              variant: "danger",
              solid: true,
              autoHideDelay: 8000,
              toaster: "b-toaster-top-center",
              toastClass: "hr-toast-alert"
            }
          );
        })
        .finally(() => {
          this.timerApiInProgress = false;
        });
    },
    userInitials(user) {
      let initials = `${user.firstName} ${user.lastName}`;
      return initials.match(/\b(\w)/g).join("");
    },
    reloadPage() {
      this.showAuthError = false;
      this.$router.go();
    },
    showCardHover(id) {
      this.activeExpandHoverId = id;
      this.expandHover = true;
      this.showInactiveExpandHover = true;
    },
    undoCardHover(id) {
      this.inactiveExpandHoverId = id;
      this.expandHover = false;
      this.inactiveArchiveHoverId = id;
    },
    navigate(cardData) {
      if (this.navigateOnClick) {
        this.$router.push({
          name: "ViewTask",
          params: {
            id: cardData.id
          }
        });
      } else {
        this.$emit('click', this)
      }
    },
    formatDueDate,
    handleCardOrder(scope) {
      scope?.$refs?.taskCardWrapper?.parentNode?.classList?.remove(
        "task-running",
        "task-paused"
      );
      scope?.$refs?.taskCardWrapper?.parentNode?.classList?.add(
        scope.timerActive ? "task-running" : "task-paused"
      );
    }
  },
  computed: {
    formattedTime() {
      let dura = moment.duration(this.currentTime, "seconds");
      return formatDuration(dura);
    },
    tooltipText() {
      return this.canUserOperateTimer ? '' : "Only task owner or admin can update timer"
    },
    canUserOperateTimer() {
      if (isAdmin()) {
        return true
      }
      let loggedInUserId = this.$store.state.authModule.profileUserId
      return this.taskOwnerId === loggedInUserId
    },
    taskOwnerId() {
      let handoverExists = this.cardData.handoverRequests.length > 0;
      if (handoverExists) {
        let handoverRequest = this.cardData.handoverRequests[0];
        if (handoverRequest.status === ACCEPTED || handoverRequest.status === NOACTION) {
          return handoverRequest.assignedTo.id;
        } else {
          return this.cardData.assignedTo;
        }
      } else {
        return this.cardData.assignedTo;
      }
    },
    ...mapState("authModule", ["profileUserId"])
  },
  data() {
    return {
      timerApiInProgress: false,
      pause: this.paused,
      statusId: "",
      expandHover: false,
      activeExpandHoverId: "",
      inactiveExpandHoverId: "",
      showAuthError: false,
      authMessage: "",
      showInactiveExpandHover: true,
      timerActive: false,
      timer: "",
      currentTime: this.time,
      statuses: statuses
    };
  },
  created() {
    if (this.time !== null && !this.pause && this.statusTitle !== "Complete") {
      this.timerActive = true;
      this.onTimerStart();
    } else {
      this.onTimerStop();
    }
    if (this.shouldPatch) {
      if (this.statusTitle === "Complete") {
        this.timerActive = false;
        const timerData = {
          action: "stop"
        };
        if (this.cardData.lastTracker.stopAt === null) {
          patchTracker(this.cardData.id, this.trackerID, timerData).then(
            res => {
              Object.assign(this.cardData.lastTracker, res.data)
              Object.assign(this.cardData.trackersLog, res.data.trackersLog)
              this.stopTimerThread()
            }
          );
        }
      }
      this.$emit("card-moved", this);
    }
  }
};
</script>

<style scoped lang="scss">
@import "@/scss/variables/colors.scss";
@import "@/scss/variables/helpers.scss";

.relative-top {
  top: 0.3rem;
}
.timer-alert {
  align-content: center;
}
.card-expand-right {
  right: 0;
}
.icons-group {
  h6 {
    margin: 0;
  }
  margin-left: auto;
}
.skeleton-icons-group {
  right: -1rem;
  top: 1rem;
}
.expand-container {
  right: 0.5rem;
  z-index: 2;
  top: 0.5rem;
}
.bottomBorder {
  border-bottom: 0;
  border-radius: 0.6rem 0.6rem 0 0;
}
.timer-card {
  border-radius: 0 0 0.5rem 0.5rem;
}
button.btn.close-btn {
  right: -2.5rem;
  top: -2rem;
  background: none;
}
.tooltiptext {
  visibility: hidden;
  background-color: $primary;
  color: white;
  text-align: center;
  border-radius: 0.8rem;
  position: absolute;
  z-index: 1;
}
div.tooltip-inner {
  padding: 8px !important;
  color: white;
  background-color: $primary-dark;
}
.tooltips:hover .tooltiptext {
  visibility: visible;
}
p {
  line-height: 2rem !important;
}
.icon-group-1,
.icon-group-2,
.icon-group-3 {
  align-items: center;
}
.icons-group div:not(:last-child) {
  padding-right: 1.5rem;
}
.card-title {
  line-height: 1.8rem !important;
  font-size: 1.3rem;
}
</style>
